
import React from 'react';
import Layout from '@/components/layout/Layout';
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const SoborgLocation = () => {
  const features = [
    {
      title: "Centralt i Søborg",
      description: "Undervisningslokaler på Niels Finsens Alle 86, 2860 Søborg, med nem adgang til offentlig transport.",
      icon: "location"
    },
    {
      title: "Fleksible tider",
      description: "Morgen-, eftermiddags- og aftentimer på hverdage samt weekendundervisning efter aftale.",
      icon: "clock"
    },
    {
      title: "Alle niveauer",
      description: "Fra nybegyndere til øvede - undervisning der er skræddersyet til dit niveau og dine mål.",
      icon: "music"
    },
    {
      title: "Konservatorieuddannet",
      description: "Undervisning af høj kvalitet fra Oscar Gilbert, som er uddannet på Rytmisk Musikkonservatorium.",
      icon: "award"
    }
  ];

  const testimonials = [
    {
      name: "Mette Jensen",
      location: "Søborg",
      text: "Oscar er en fantastisk underviser. Hans tålmodighed og engagement har hjulpet mig med at opnå fremskridt, jeg aldrig troede var mulige.",
      stars: 5
    },
    {
      name: "Thomas Nielsen",
      location: "Søborg",
      text: "Min datter har gået til klaverundervisning hos Oscar i to år nu, og hun elsker det. Han formår at gøre undervisningen sjov og inspirerende.",
      stars: 5
    },
    {
      name: "Karen Petersen",
      location: "Søborg",
      text: "Som voksen nybegynder var jeg nervøs for at starte, men Oscar har et utroligt talent for at få en til at føle sig tryg og motiveret.",
      stars: 5
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Klaverundervisning i Søborg | Få en gratis prøvetime!</title>
        <meta name="description" content="Professionel klaverundervisning i Søborg med Oscar Gilbert. Book en gratis prøvetime og start din musikalske rejse i dag!" />
      </Helmet>
      
      <LocationHero 
        title="Klaverundervisning i Søborg"
        description="Professionel klaverundervisning med Oscar Gilbert i hjertet af Søborg. Få en personlig tilgang til at lære klaver i behagelige omgivelser på Niels Finsens Alle 86."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Klaverundervisning i <span className="text-piano-gold">Søborg</span> med Oscar Gilbert
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                På Niels Finsens Alle 86 i Søborg har Oscar Gilbert indrettet et professionelt undervisningslokale, hvor elever i alle aldre og på alle niveauer kan udforske musikken og udvikle deres klaverfærdigheder.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Undervisningen foregår på et akustisk Yamaha-flygel i et lyst og inspirerende lokale, der skaber de perfekte rammer for en optimal læringsoplevelse.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Som konservatorieuddannet pianist fra Rytmisk Musikkonservatorium med mange års undervisningserfaring tilbyder Oscar en struktureret og personlig tilgang til klaverundervisning, der er skræddersyet til dine individuelle behov og musikalske mål.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Book Gratis Prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/om">Læs Mere Om Oscar</Link>
                </Button>
              </div>
              <div className="mt-6">
                <a 
                  href="https://dk.trustpilot.com/review/oscargilbert.dk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-gray-600 hover:text-piano-gold"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  <span>Se anmeldelser på Trustpilot</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
              <div className="relative bg-white p-3 rounded-lg shadow-xl">
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" 
                    alt="Klaverundervisning i Søborg" 
                    className="w-full h-full object-cover object-center object-[50%_35%]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Søborg" title="Hvad siger mine elever i Søborg" />
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Find vej til undervisningen</h2>
              <p className="text-lg text-gray-600">
                Klaverundervisningen foregår på Niels Finsens Alle 86, 2860 Søborg, centralt i Søborg.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl mb-10">
              <div className="overflow-hidden rounded-md">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12607.679143124358!2d12.514096630724325!3d55.734728901797794!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743668909093!5m2!1sda!2sdk" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokation på kort">
                </iframe>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Adresse</h3>
                  <p className="text-gray-700">
                    Niels Finsens Alle 86<br />
                    2860 Søborg
                  </p>
                  <div className="space-y-2">
                    <p className="text-gray-700"><strong>Transport:</strong></p>
                    <p className="text-gray-700">• 5 minutters gang fra Vangede Station</p>
                    <p className="text-gray-700">• Gode busforbindelser</p>
                    <p className="text-gray-700">• Gratis parkering foran bygningen</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Kontakt</h3>
                  <p className="text-gray-700">
                    Telefon: <a href="tel:+4540817127" className="text-piano-gold hover:underline">+45 40 81 71 27</a>
                  </p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:kontakt@pianoundervisning.dk" className="text-piano-gold hover:underline">kontakt@pianoundervisning.dk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationCTA locationName="Søborg" />
    </Layout>
  );
};

export default SoborgLocation;
